import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import ProductRating from '../components/common/ProductRating';
import { imagePath } from '../utils/imagePath';
import storeApi from '../services/storeApi';

/**
 * ComparePage - Product comparison page
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const ComparePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // TODO: Get products from localStorage or API for comparison
    // For now, fetch some sample products
    const fetchProducts = async () => {
      try {
        const data = await storeApi.getProducts({ page_size: 2 });
        const productList = data.results || data;
        setProducts(productList.slice(0, 2));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleAddToCart = async (productId) => {
    try {
      await storeApi.addToCart(productId, 1);
      // Trigger cart update event for header
      window.dispatchEvent(new Event('cartUpdated'));
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to cart');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading products...</p>
        </div>
      </PageContainer>
    );
  }

  if (products.length === 0) {
    return (
      <PageContainer>
        <div className="breadcrumb-area">
          <div className="container">
            <div className="breadcrumb-content">
              <ul>
                <li><Link to="/store">Home</Link></li>
                <li className="active">Compare</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="compare-area pt-60 pb-60">
          <div className="container">
            <div className="text-center py-5">
              <p>No products to compare. Add products to compare from product pages.</p>
              <Link to="/store/shop" className="btn">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="breadcrumb-area">
        <div className="container">
          <div className="breadcrumb-content">
            <ul>
              <li><Link to="/store">Home</Link></li>
              <li className="active">Compare</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Compare Area */}
      <div className="compare-area pt-60 pb-60">
        <div className="container">
          <div className="compare-table table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <tbody>
                {/* Image Row */}
                <tr>
                  <th className="compare-column-titles">Image</th>
                  {products.map((product) => (
                    <td key={product.id} className="compare-column-productinfo">
                      <div className="compare-pdoduct-image">
                        <Link to={`/store/product/${product.slug}`}>
                          <img
                            src={product.primary_image_url || imagePath('product/large-size/1.jpg')}
                            alt={product.name}
                            onError={(e) => {
                              e.target.src = imagePath('product/large-size/1.jpg');
                            }}
                          />
                        </Link>
                        <button
                          className="ho-button ho-button-sm"
                          onClick={() => handleAddToCart(product.id)}
                        >
                          <span>ADD TO CART</span>
                        </button>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Product Name Row */}
                <tr>
                  <th>Product Name</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      <h5 className="compare-product-name">
                        <Link to={`/store/product/${product.slug}`}>{product.name}</Link>
                      </h5>
                    </td>
                  ))}
                </tr>

                {/* Description Row */}
                <tr>
                  <th>Description</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      {product.short_description || product.description?.substring(0, 100) || 'No description available'}
                    </td>
                  ))}
                </tr>

                {/* Price Row */}
                <tr>
                  <th>Price</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      PKR {parseFloat(product.price).toFixed(2)}
                      {product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price) && (
                        <span style={{ textDecoration: 'line-through', marginLeft: '10px', color: '#999' }}>
                          PKR {parseFloat(product.compare_at_price).toFixed(2)}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Stock Row */}
                <tr>
                  <th>Stock</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      {product.is_in_stock ? (
                        <span className="text-success">In Stock</span>
                      ) : (
                        <span className="text-danger">Out of Stock</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Rating Row */}
                <tr>
                  <th>Rating</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      <ProductRating rating={product.average_rating || 0} />
                    </td>
                  ))}
                </tr>

                {/* SKU Row */}
                <tr>
                  <th>SKU</th>
                  {products.map((product) => (
                    <td key={product.id}>{product.sku || 'N/A'}</td>
                  ))}
                </tr>

                {/* Category Row */}
                <tr>
                  <th>Category</th>
                  {products.map((product) => (
                    <td key={product.id}>
                      {product.category ? (
                        <Link to={`/store/shop/${product.category.slug}`}>
                          {product.category.name}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ComparePage;
